<template>
  <b-card header-tag="header" class="m-3" no-body>
    <template #header>
      <header-sagres
        titulo="Cassiq - Controle, Análise e Supervisão dos Sistemas de Informação e Qualidade do PNTP"
        breakpoint="xl"
      />
    </template>
    <b-card-body>
      <CardFiltrosBusca>
        <b-form
          @submit.prevent="carregarAvaliacao"
          @reset.prevent="limparFiltros"
        >
          <b-row>
            <b-col cols="12" xl="6">
              <default-form-validation-provider
                label="Unidade Gestora"
                v-slot="defaultState"
              >
                <b-overlay :show="loadingUG">
                  <multiselect
                    v-model="ugResumida"
                    open-direction="bottom"
                    :close-on-select="true"
                    :searchable="true"
                    :state="defaultState.state"
                    deselect-label=""
                    :allow-empty="true"
                    placeholder="Selecione uma UG"
                    track-by="codUgSagres"
                    :options="listaUGs"
                    label="nmNome"
                    :show-labels="false"
                    class="wrapper-multiselect-sagres"
                    @select="carregarDatasAvaliacao"
                  >
                    <template slot="noOptions">
                      <small><i>Erro ao obter dados.</i></small>
                    </template>
                    <template slot="noResult">
                      <i>Sem resultados.</i>
                    </template>
                  </multiselect>
                </b-overlay>
              </default-form-validation-provider>
            </b-col>
            <b-col cols="12" xl="6">
              <default-form-validation-provider
                label="Data da avaliação"
                v-slot="defaultState"
              >
                <b-overlay :show="loadingDataAvaliacao">
                  <b-form-select
                    :disabled="!ugResumida || !ugResumida.codUgSagres"
                    id="select-data-avaliacao"
                    :options="dataAvaliacaoOptions"
                    v-model="idDataAvaliacao"
                    :state="defaultState.state"
                  />
                </b-overlay>
              </default-form-validation-provider>
            </b-col>
            <b-col cols="6" xl="3" offset-xl="3">
              <b-button type="reset" variant="outline-secondary">
                <font-awesome-icon icon="redo" class="mr-3" />
                Limpar
              </b-button>
            </b-col>
            <b-col cols="6" xl="3">
              <b-button
                type="submit"
                :disabled="tableBusy || !ugResumida || !ugResumida.codUgSagres"
              >
                <font-awesome-icon icon="search" class="mr-3" />
                Filtrar
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </CardFiltrosBusca>
      <span v-if="totalAuditado" class="font-weight-bold"
        >Total auditado: {{ totalAuditado }}</span
      >
      <b-table
        class="cassiq-table"
        :fields="fields"
        :items="items"
        :busy="tableBusy"
        hover
        no-border-collapse
        sort-by=""
        :sort-desc="true"
        striped
        small
        stacked="lg"
        responsive
        show-empty
      >
        <template #empty>
          <span>Não foram encontrados dados.</span>
        </template>
        <template #emptyfiltered>
          <span>Não foram encontrados dados.</span>
        </template>
        <template #table-busy>
          <div class="text-center text-dark">
            <b-spinner class="align-middle"></b-spinner>
            <strong class="loading-text">Carregando...</strong>
          </div>
        </template>
        <template v-slot:cell(actions)="row">
          <div class="actions-icons">
            <b-link class="text-dark" @click="row.toggleDetails">
              <font-awesome-icon
                :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'"
              />
              <div class="cassiq-action-label d-sm-none">Detalhes</div>
            </b-link>
          </div>
        </template>

        <template #row-details="row">
          <TabelaValidacoesSerigy
            :criterios="row.item.criterios"
            :respostasOutrasAvaliacoes="respostasOutrasAvaliacoes"
            :nomeAuditoresDiferentes="nomeAuditoresDiferentes"
            :idAvaliacao="idDataAvaliacao"
            @reload="carregarAvaliacao"
          />
        </template>
      </b-table>
    </b-card-body>
  </b-card>
</template>

<script lang="ts">
import CardFiltrosBusca from "@/components/shared/CardFiltrosBusca.vue";
import HeaderSagres from "@/components/shared/HeaderSagres.vue";
import serigyService from "@/services/administracao/serigyService";
import { store } from "@/store";
import { fieldsGeralSerigy } from "@/types/administracao/serigy/fields-geral-serigy";
import { SelectValue } from "@/types/select-value";
import { TableField } from "@/types/table-field";
import { UtilsTS } from "@/utilsts";
import { mixins } from "vue-class-component";
import { Component, Vue } from "vue-property-decorator";
import DefaultFormValidationProvider from "@/components/shared/DefaultFormValidationProvider.vue";
import Multiselect from "vue-multiselect";
import { RespostaSerigy } from "@/types/administracao/serigy/resposta-serigy";
import TabelaValidacoesSerigy from "@/components/administracao/serigy/TabelaValidacoesSerigy.vue";
import { RespostasOutrasAvaliacoes } from "@/types/administracao/serigy/respostas-outras-avaliacoes";
import { AuditoresDiferentes } from "@/types/administracao/serigy/auditores-diferentes";
import usuarioService from "@/services/administracao/usuarioService";
import { Ug } from "@/types/administracao/serigy/ug-serigy/ug";
import { Avaliacao } from "@/types/administracao/serigy/invoker/avaliacao";

const CompProps = Vue.extend({
  name: "Cassiq",
  components: {
    CardFiltrosBusca,
    DefaultFormValidationProvider,
    HeaderSagres,
    Multiselect,
    TabelaValidacoesSerigy,
  },
});

@Component
export default class Cassiq extends mixins(UtilsTS, CompProps) {
  listaUGs: Ug[] = [];
  ugResumida: Ug | null = null;

  idDataAvaliacao: number | null = null;
  dataAvaliacaoOptions: SelectValue[] = [];

  totalAuditado = "";
  items: RespostaSerigy[] = [];
  fields: TableField[] = fieldsGeralSerigy;
  respostasOutrasAvaliacoes: RespostasOutrasAvaliacoes[] = [];
  nomeAuditoresDiferentes: AuditoresDiferentes[] = [];

  detalhe = false;
  tableBusy = false;
  loadingUG = false;
  loadingDataAvaliacao = false;

  cpfAuditor: string | null = null;

  async carregarAvaliacao(): Promise<void> {
    if (!this.idDataAvaliacao) return;
    try {
      this.tableBusy = true;
      const response = await serigyService.getRespostasByAvaliacaoId(
        this.idDataAvaliacao
      );
      const qntdResp = response.qtdRespostas;
      this.totalAuditado =
        qntdResp.respostasAuditor > 0
          ? `${(
                  (qntdResp.respostasAuditor / qntdResp.respostasBot) *
                  100
                ).toFixed(2)
              }%`
          : "0%";
      this.items = response.respostas;

      const cpfsAuditoresDiferentes = new Set<string>();
      this.items.forEach((item) => {
        item.criterios.forEach((criterio) => {
          if (criterio.cpfAuditor && criterio.cpfAuditor !== this.cpfAuditor)
            cpfsAuditoresDiferentes.add(criterio.cpfAuditor);
        });
      });

      this.nomeAuditoresDiferentes =
        cpfsAuditoresDiferentes && cpfsAuditoresDiferentes.size > 0
          ? await this.getNomeAuditoresDiferentes(cpfsAuditoresDiferentes)
          : [];

      this.respostasOutrasAvaliacoes =
        response.respostasOutrasAvaliacoes &&
        response.respostasOutrasAvaliacoes.length > 0
          ? await this.getRespOutrasAvaByCriterio(
              response.respostasOutrasAvaliacoes
            )
          : [];

    } catch (e: unknown) {
      this.tratarError(e);
      this.items = [];
      this.nomeAuditoresDiferentes = [];
      this.respostasOutrasAvaliacoes = [];
    } finally {
      this.tableBusy = false;
    }
  }

  async getNomeAuditoresDiferentes(
    cpfs: Set<string>
  ): Promise<AuditoresDiferentes[]> {
    try {
      const response = await usuarioService.getUsuariosByCpfs(Array.from(cpfs));
      return response.map((auditor) => new AuditoresDiferentes(auditor));
    } catch (error: unknown) {
      this.tratarError(error);
      return [];
    }
  }

  async getRespOutrasAvaByCriterio(
    respOutrasAva: RespostasOutrasAvaliacoes[]
  ): Promise<RespostasOutrasAvaliacoes[]> {
    try {
      const response = await usuarioService.getUsuariosByCpfs(
        respOutrasAva.map((r) => r.cpfAuditor)
      );
      return respOutrasAva.map((resp) => {
        resp.nomeAuditor =
          response.find((u) => u.Cpf === resp.cpfAuditor)?.Nome ??
          "Usuário não encontrado";
        return new RespostasOutrasAvaliacoes(resp);
      });
    } catch (error: unknown) {
      this.tratarError(error);
      return [];
    }
  }

  async carregarDatasAvaliacao(): Promise<void> {
    if (!this.ugResumida || !this.ugResumida.codUgSagres) return;
    try {
      this.dataAvaliacaoOptions = [];
      this.loadingDataAvaliacao = true;
      const response = await serigyService.listarAvaliacoesPorUg(
        this.ugResumida.codUgSagres
      );
      this.dataAvaliacaoOptions = response.map((ava: Avaliacao) => ({
        text: new Date(ava.dhAvaliacao).toLocaleDateString(),
        value: ava.id,
      }));
      this.dataAvaliacaoOptions.unshift({
        text: "Selecione uma data",
        value: null,
      });
      if (
        this.dataAvaliacaoOptions[1] === undefined ||
        (this.dataAvaliacaoOptions[1] !== undefined &&
          this.dataAvaliacaoOptions[1].value === undefined)
      ) {
        this.$toast.error(
          "Não foram encontradas avaliações para a UG selecionada."
        );
        return;
      }
      this.idDataAvaliacao = Number(this.dataAvaliacaoOptions[1].value);
      await this.carregarAvaliacao();
    } catch (e: unknown) {
      this.tratarError(e);
    } finally {
      this.loadingDataAvaliacao = false;
    }
  }

  async carregarUGs(): Promise<void> {
    try {
      this.loadingUG = true;
      this.listaUGs = await serigyService.listarUgs();
    } catch (e: unknown) {
      this.tratarError(e);
    } finally {
      this.ugResumida = null;
      this.loadingUG = false;
    }
  }

  async limparFiltros(): Promise<void> {
    this.ugResumida = null;
    this.idDataAvaliacao = null;
  }

  async mounted(): Promise<void> {
    try {
      store.blockScreen = true;
      this.carregarUGs();
      this.cpfAuditor = localStorage.getItem("cpf");
    } catch (e) {
      console.error(e);
    } finally {
      store.blockScreen = false;
    }
  }
}
</script>
